/**
 * Triangle Checkers (Chinese Checkers Style) Game Logic
 * Uses a hexagonal star-shaped board with 6 points
 * Players start in opposite points and race to the other side
 */

// Board layout: 6-pointed star (121 cells total)
// Using axial coordinates for hex grid

export const TRIANGLE_VARIANT = {
  name: 'Triangle Checkers',
  boardType: 'hexStar',
  piecesPerPlayer: 10,
  playerCount: 2,
  description: 'Race your pieces to the opposite corner'
};

// Hex directions for 6-direction movement
const HEX_DIRS = [
  { q: 1, r: 0 },   // East
  { q: 1, r: -1 },  // Northeast  
  { q: 0, r: -1 },  // Northwest
  { q: -1, r: 0 },  // West
  { q: -1, r: 1 },  // Southwest
  { q: 0, r: 1 }    // Southeast
];

// Generate the valid cells for a 6-pointed star board (Chinese Checkers style)
// Total: 121 cells = 61 center + 10*6 points
export function generateStarBoard() {
  const cells = new Map();
  
  // Central hexagon with radius 4 (61 cells)
  const radius = 4;
  for (let q = -radius; q <= radius; q++) {
    const r1 = Math.max(-radius, -q - radius);
    const r2 = Math.min(radius, -q + radius);
    for (let r = r1; r <= r2; r++) {
      cells.set(`${q},${r}`, { q, r, zone: 'center' });
    }
  }
  
  // Star points - each is a triangle of 10 cells (rows of 4,3,2,1 from base to tip)
  // The 6 points are positioned at the "flat" edges of the hexagon in pointy-top orientation
  
  // Point 0: TOP (Player 1 home) - extends upward from top edge
  // Base row touches r = -radius, tip is at r = -radius - 4
  for (let dist = 1; dist <= 4; dist++) {
    // At distance dist from center edge, we have (5-dist) cells
    const numCells = 5 - dist;
    const r = -radius - dist;
    const startQ = -Math.floor((numCells - 1) / 2);
    for (let i = 0; i < numCells; i++) {
      const q = startQ + i;
      cells.set(`${q},${r}`, { q, r, zone: 'point0' });
    }
  }
  
  // Point 3: BOTTOM (Player 2 home) - opposite of Point 0
  // Mirror of Point 0, extends downward
  for (let dist = 1; dist <= 4; dist++) {
    const numCells = 5 - dist;
    const r = radius + dist;
    const startQ = Math.floor((numCells - 1) / 2);
    for (let i = 0; i < numCells; i++) {
      const q = startQ - i;
      cells.set(`${q},${r}`, { q, r, zone: 'point3' });
    }
  }
  
  // Point 1: TOP-RIGHT - extends from top-right diagonal edge
  for (let dist = 1; dist <= 4; dist++) {
    const numCells = 5 - dist;
    for (let i = 0; i < numCells; i++) {
      const q = radius + dist - i;
      const r = -radius - dist + 1 + i;
      cells.set(`${q},${r}`, { q, r, zone: 'point1' });
    }
  }
  
  // Point 4: BOTTOM-LEFT - opposite of Point 1
  for (let dist = 1; dist <= 4; dist++) {
    const numCells = 5 - dist;
    for (let i = 0; i < numCells; i++) {
      const q = -radius - dist + i;
      const r = radius + dist - 1 - i;
      cells.set(`${q},${r}`, { q, r, zone: 'point4' });
    }
  }
  
  // Point 2: BOTTOM-RIGHT
  for (let dist = 1; dist <= 4; dist++) {
    const numCells = 5 - dist;
    for (let i = 0; i < numCells; i++) {
      const q = radius + i;
      const r = dist - i;
      cells.set(`${q},${r}`, { q, r, zone: 'point2' });
    }
  }
  
  // Point 5: TOP-LEFT - opposite of Point 2
  for (let dist = 1; dist <= 4; dist++) {
    const numCells = 5 - dist;
    for (let i = 0; i < numCells; i++) {
      const q = -radius - i;
      const r = -dist + i;
      cells.set(`${q},${r}`, { q, r, zone: 'point5' });
    }
  }
  
  return cells;
}

// Hex directions for movement (6 directions)
const HEX_DIRECTIONS = [
  { dq: 1, dr: 0 },   // East
  { dq: 1, dr: -1 },  // Northeast
  { dq: 0, dr: -1 },  // Northwest
  { dq: -1, dr: 0 },  // West
  { dq: -1, dr: 1 },  // Southwest
  { dq: 0, dr: 1 }    // Southeast
];

export class TriangleCheckersGame {
  constructor() {
    this.cells = generateStarBoard();
    this.pieces = new Map(); // key: "q,r", value: { player: 1 or 2 }
    this.currentPlayer = 1;
    this.gameOver = false;
    this.winner = null;
    this.moveHistory = [];
    this.selectedPiece = null;
    this.validMoves = [];
    
    this.initializePieces();
  }
  
  initializePieces() {
    this.pieces.clear();
    
    // Player 1 pieces in Point 0 (top)
    for (const [key, cell] of this.cells) {
      if (cell.zone === 'point0') {
        this.pieces.set(key, { player: 1 });
      }
    }
    
    // Player 2 pieces in Point 3 (bottom)
    for (const [key, cell] of this.cells) {
      if (cell.zone === 'point3') {
        this.pieces.set(key, { player: 2 });
      }
    }
  }
  
  getCellKey(q, r) {
    return `${q},${r}`;
  }
  
  parseKey(key) {
    const [q, r] = key.split(',').map(Number);
    return { q, r };
  }
  
  isValidCell(q, r) {
    return this.cells.has(this.getCellKey(q, r));
  }
  
  getPiece(q, r) {
    return this.pieces.get(this.getCellKey(q, r));
  }
  
  getValidMoves(q, r) {
    const piece = this.getPiece(q, r);
    if (!piece || piece.player !== this.currentPlayer) return [];
    
    const moves = [];
    const visited = new Set();
    
    // Simple moves (step to adjacent empty cell)
    for (const dir of HEX_DIRECTIONS) {
      const newQ = q + dir.dq;
      const newR = r + dir.dr;
      
      if (this.isValidCell(newQ, newR) && !this.getPiece(newQ, newR)) {
        moves.push({
          q: newQ,
          r: newR,
          isJump: false,
          path: [{ q, r }, { q: newQ, r: newR }]
        });
      }
    }
    
    // Jump moves (hop over pieces - can chain)
    this.findJumps(q, r, [], visited, moves);
    
    return moves;
  }
  
  findJumps(q, r, currentPath, visited, allMoves) {
    const key = this.getCellKey(q, r);
    if (visited.has(key)) return;
    visited.add(key);
    
    const startPath = currentPath.length === 0 
      ? [{ q, r }] 
      : [...currentPath];
    
    for (const dir of HEX_DIRECTIONS) {
      const midQ = q + dir.dq;
      const midR = r + dir.dr;
      const endQ = q + dir.dq * 2;
      const endR = r + dir.dr * 2;
      
      // Check if there's a piece to jump over and empty landing spot
      if (
        this.isValidCell(midQ, midR) &&
        this.getPiece(midQ, midR) &&
        this.isValidCell(endQ, endR) &&
        !this.getPiece(endQ, endR) &&
        !visited.has(this.getCellKey(endQ, endR))
      ) {
        const newPath = [...startPath, { q: endQ, r: endR }];
        
        allMoves.push({
          q: endQ,
          r: endR,
          isJump: true,
          path: newPath
        });
        
        // Recursively find more jumps from the landing spot
        const newVisited = new Set(visited);
        this.findJumps(endQ, endR, newPath, newVisited, allMoves);
      }
    }
  }
  
  makeMove(fromQ, fromR, toQ, toR) {
    const piece = this.getPiece(fromQ, fromR);
    if (!piece) return false;
    
    const validMoves = this.getValidMoves(fromQ, fromR);
    const move = validMoves.find(m => m.q === toQ && m.r === toR);
    
    if (!move) return false;
    
    // Execute move
    const fromKey = this.getCellKey(fromQ, fromR);
    const toKey = this.getCellKey(toQ, toR);
    
    this.pieces.delete(fromKey);
    this.pieces.set(toKey, piece);
    
    // Record move
    this.moveHistory.push({
      player: this.currentPlayer,
      from: { q: fromQ, r: fromR },
      to: { q: toQ, r: toR },
      isJump: move.isJump,
      path: move.path
    });
    
    // Check for win
    this.checkGameOver();
    
    if (!this.gameOver) {
      this.switchPlayer();
    }
    
    return true;
  }
  
  switchPlayer() {
    this.currentPlayer = this.currentPlayer === 1 ? 2 : 1;
  }
  
  checkGameOver() {
    // Player 1 wins when all pieces are in Point 3 (bottom)
    // Player 2 wins when all pieces are in Point 0 (top)
    
    let player1InGoal = 0;
    let player2InGoal = 0;
    let player1Total = 0;
    let player2Total = 0;
    
    for (const [key, piece] of this.pieces) {
      const cell = this.cells.get(key);
      if (!cell) continue;
      
      if (piece.player === 1) {
        player1Total++;
        if (cell.zone === 'point3') {
          player1InGoal++;
        }
      } else {
        player2Total++;
        if (cell.zone === 'point0') {
          player2InGoal++;
        }
      }
    }
    
    // Check win conditions
    if (player1InGoal === player1Total && player1Total > 0) {
      this.gameOver = true;
      this.winner = 1;
    } else if (player2InGoal === player2Total && player2Total > 0) {
      this.gameOver = true;
      this.winner = 2;
    }
    
    return this.gameOver;
  }
  
  getAllValidMoves() {
    const allMoves = [];
    
    for (const [key, piece] of this.pieces) {
      if (piece.player === this.currentPlayer) {
        const { q, r } = this.parseKey(key);
        const moves = this.getValidMoves(q, r);
        for (const move of moves) {
          allMoves.push({
            from: { q, r },
            to: move
          });
        }
      }
    }
    
    return allMoves;
  }
  
  getState() {
    return {
      pieces: Array.from(this.pieces.entries()),
      currentPlayer: this.currentPlayer,
      gameOver: this.gameOver,
      winner: this.winner,
      moveHistory: [...this.moveHistory]
    };
  }
  
  loadState(state) {
    this.pieces = new Map(state.pieces);
    this.currentPlayer = state.currentPlayer;
    this.gameOver = state.gameOver;
    this.winner = state.winner;
    this.moveHistory = [...state.moveHistory];
  }
  
  clone() {
    const cloned = new TriangleCheckersGame();
    cloned.loadState(this.getState());
    return cloned;
  }
  
  // Get piece counts for display
  getPieceCounts() {
    let player1 = 0;
    let player2 = 0;
    let player1InGoal = 0;
    let player2InGoal = 0;
    
    for (const [key, piece] of this.pieces) {
      const cell = this.cells.get(key);
      if (piece.player === 1) {
        player1++;
        if (cell?.zone === 'point3') player1InGoal++;
      } else {
        player2++;
        if (cell?.zone === 'point0') player2InGoal++;
      }
    }
    
    return { player1, player2, player1InGoal, player2InGoal };
  }
  
  // Get board layout for rendering
  getBoardLayout() {
    // Convert hex coordinates to pixel positions
    const layout = [];
    const hexSize = 20; // Size of each hex
    
    for (const [key, cell] of this.cells) {
      const piece = this.pieces.get(key);
      
      // Convert axial to pixel coordinates
      const x = hexSize * (3/2 * cell.q);
      const y = hexSize * (Math.sqrt(3) * (cell.r + cell.q/2));
      
      layout.push({
        key,
        q: cell.q,
        r: cell.r,
        x,
        y,
        zone: cell.zone,
        piece: piece || null
      });
    }
    
    return layout;
  }
}

export default TriangleCheckersGame;
